import { z } from 'zod';

/**
 * What the player agrees to, and which version of it. The database keeps an append-only log
 * (`consents`); this is the vocabulary and the rules on top: which versions are current, which
 * consents are needed to play online, and which are never assumed.
 */

export const CONSENT_PURPOSES = ['terms', 'privacy', 'analytics'] as const;
export const ConsentPurposeSchema = z.enum(CONSENT_PURPOSES);
export type ConsentPurpose = z.infer<typeof ConsentPurposeSchema>;

/**
 * The versions in force. A document (or the analytics notice) that changes in substance gets a new
 * date here; whoever accepted an older one is asked again. DRAFT: the legal texts have not been
 * reviewed yet (docs/STATUS.md), so these dates only mark what the app currently shows.
 */
export const CONSENT_VERSIONS: Record<ConsentPurpose, string> = {
  terms: '2026-10-20-draft',
  privacy: '2026-10-20-draft',
  analytics: '1',
};

/** Needed to play online or to keep a cloud account. Analytics is never needed. */
export const REQUIRED_CONSENTS: readonly ConsentPurpose[] = ['terms', 'privacy'];

/** The latest entry of a purpose, as the `current_consents` view gives it. */
export interface ConsentState {
  purpose: ConsentPurpose;
  version: string;
  granted: boolean;
  at?: string;
}

export type ConsentMap = Partial<Record<ConsentPurpose, ConsentState>>;

export function consentMap(entries: readonly ConsentState[]): ConsentMap {
  const map: ConsentMap = {};
  for (const e of entries) map[e.purpose] = e;
  return map;
}

/** True when the purpose is granted at the version in force. */
export function isGranted(map: ConsentMap, purpose: ConsentPurpose): boolean {
  const c = map[purpose];
  return c !== undefined && c.granted && c.version === CONSENT_VERSIONS[purpose];
}

/** The required consents the player still owes (never given, withdrawn, or given for an old version). */
export function missingConsents(map: ConsentMap): ConsentPurpose[] {
  return REQUIRED_CONSENTS.filter((p) => !isGranted(map, p));
}

/** Playing online needs the terms and the privacy notice. */
export function canPlayOnline(map: ConsentMap): boolean {
  return missingConsents(map).length === 0;
}

/** Analytics only runs with an explicit, current yes. */
export function analyticsAllowed(map: ConsentMap): boolean {
  return isGranted(map, 'analytics');
}
